import {FaLocationArrow} from "react-icons/fa";
import {FiThermometer, FiWind, FiMapPin} from "react-icons/fi";
import {getWindDirection} from "../../utils";
import {Link, useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import {getNow} from "../../services/api";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import { getFlightSafetyStatus, getFlightStatusLabel, getFlightStatusBorderClasses, getFlightStatusBadgeClasses } from "../../utils/flightSafety";
import { TakeOffSiteBoxSkeleton } from "../Skeleton";

const TakeOffSiteBox = ({takeOffSite, weatherData, controlled = false}) => {
    const isSnap = navigator.userAgent === 'ReactSnap';
    const {t} = useTranslation();

    const navigate = useNavigate();
    const [fetchedData, setFetchedData] = useState(null);
    const stationData = controlled ? weatherData : fetchedData;

    useEffect(() => {
        if (controlled || !takeOffSite) return;
        (async () => {
            const now = await getNow(takeOffSite.id);
            setFetchedData(now);
        })();
    }, [takeOffSite, controlled]);

    // Get flight safety status using the utility function
    const flightStatus = getFlightSafetyStatus(takeOffSite, stationData);
    const { isOffline, isSuitableWind, isWindTooStrong } = flightStatus;



    const renderWindData = () => {
        if (!stationData) {
            return null;
        }
        return (
            <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="flex flex-col items-center">
                    <FaLocationArrow 
                        style={{transform: `rotate(${parseInt(stationData?.WIND_ANG) + 135 || 135}deg)`}} 
                        className='text-blue-600 dark:text-blue-400' 
                        size={12}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{getWindDirection(stationData.WIND_ANG)}</span>
                </div>
                <div className="flex items-center space-x-1">
                    <FiWind className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{Math.round(stationData.WIND_SP || 0)}</span>
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('m/s')}</span>
                </div>
            </div>
        )
    }

    const renderTempData = () => {
        if (!stationData) {
            return null;
        }
        return (
            <div className="flex items-center space-x-1">
                <FiThermometer className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600 dark:text-gray-400" />
                <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{Math.round(stationData.TEMPERATURE || 0)}</span>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">°C</span>
            </div>
        )
    }
    // Show skeleton while loading weather data
    if (!stationData && takeOffSite) {
        return <TakeOffSiteBoxSkeleton />;
    }

    return (
        <div className="w-full sm:w-1/2 2xl:w-1/3 p-2 sm:p-3">
            {isSnap && !isOffline && <Link to={`/site/${takeOffSite?.slug}`}/>}
            <div
                className={`bg-white dark:bg-slate-800 rounded-xl shadow-lg border-2 overflow-hidden transition-all duration-200 hover:shadow-xl hover:scale-[102%] cursor-pointer ${getFlightStatusBorderClasses(flightStatus.status)}`}
                onClick={() => navigate(`/site/${takeOffSite?.slug}`)}
            >
                {/* Background Image Section */}
                <div 
                    className="h-40 relative"
                    style={{
                        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4)), url('/assets/images/${takeOffSite.image}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    {/* Offline overlay */}
                    {isOffline && (
                        <div className="absolute inset-0 bg-red-500/40"></div>
                    )}
                    {/* Site Name Badge */}
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
                        <div className={`backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-lg ${
                            isOffline 
                                ? 'bg-red-500/90 sm:bg-white/95 sm:dark:bg-slate-900/95' 
                                : 'bg-white/95 dark:bg-slate-900/95'
                        }`}>
                            <div className="flex items-center space-x-1 sm:space-x-2">
                                <FiMapPin className={`w-3 sm:w-4 h-3 sm:h-4 ${
                                    isOffline 
                                        ? 'text-white sm:text-blue-600 sm:dark:text-blue-400' 
                                        : 'text-blue-600 dark:text-blue-400'
                                }`} />
                                <h3 className={`font-semibold text-xs sm:text-sm ${
                                    isOffline 
                                        ? 'text-white sm:text-gray-900 sm:dark:text-white' 
                                        : 'text-gray-900 dark:text-white'
                                }`}>
                                    {t(takeOffSite?.name) || 'N/A'}
                                </h3>
                                {/* Mobile pulsing dot indicator */}
                                {isOffline && (
                                    <div className="sm:hidden w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Status badges */}
                    {(isOffline || isWindTooStrong || isSuitableWind) && (
                        <div className="absolute top-3 right-3 hidden sm:block">
                            <div className={`${getFlightStatusBadgeClasses(flightStatus.status)} backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg ${isOffline ? 'animate-pulse' : ''}`}>
                                <div className="text-white text-xs font-medium">
                                    {t(getFlightStatusLabel(flightStatus.status))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Weather Data Section */}
                <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                    {isOffline ? (
                        <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
                            <div className="text-center">
                                <div className="text-xs sm:text-sm">{t('Weather station is not available now')}</div>
                                <div className="text-xs mt-1 opacity-70">{t('Click to view site details')}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center">
                            <div className="text-gray-700 dark:text-gray-300">
                                {renderWindData()}
                            </div>
                            <div className="text-gray-700 dark:text-gray-300">
                                {renderTempData()}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default TakeOffSiteBox;
